import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    Mail, Send, ArrowLeft, Search, RefreshCw,
    PenSquare, Clock, ChevronRight, MailOpen, SendHorizonal
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = parseISO(dateStr);
        if (!isValid(d)) return dateStr;
        const now = new Date();
        const diffMs = now - d;
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffDays === 0) return format(d, 'HH:mm');
        if (diffDays === 1) return 'Hier';
        if (diffDays < 7) return format(d, 'EEEE', { locale: fr });
        return format(d, 'dd/MM/yyyy');
    } catch { return dateStr; }
}

function formatFullDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = parseISO(dateStr);
        if (!isValid(d)) return dateStr;
        return format(d, "d MMMM yyyy 'a' HH:mm", { locale: fr });
    } catch { return dateStr; }
}

function stripHtml(html) {
    if (!html) return '';
    // Sanitize the HTML first to avoid running scripts or loading remote
    // resources just to extract plain text. We also forbid the parsing of
    // event handler attributes for defense-in-depth.
    const safe = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true,
    });
    return safe.replace(/\s+/g, ' ').trim();
}

export default function Messagerie() {
    const { canWrite: canWriteMail } = useCan('messagerie');
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCompose, setShowCompose] = useState(false);
    const [composeForm, setComposeForm] = useState({ to: '', subject: '', body: '' });
    const [composeSending, setComposeSending] = useState(false);

    const fetchSentEmails = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const res = await axios.get(`${API_URL}/api/emails/sent`, { withCredentials: true });
            setEmails(res.data || []);
        } catch {
            if (!silent) toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchSentEmails();
    }, [fetchSentEmails]);

    const filteredEmails = emails.filter(e => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        if (e.to_email?.toLowerCase().includes(term)) return true;
        if (e.client_name?.toLowerCase().includes(term)) return true;
        if (e.subject?.toLowerCase().includes(term)) return true;
        if (stripHtml(e.html_body || '').toLowerCase().includes(term)) return true;
        return false;
    });

    const handleCompose = async (e) => {
        e.preventDefault();
        if (!composeForm.to.trim() || !composeForm.subject.trim()) {
            toast.error('Email et sujet requis');
            return;
        }
        setComposeSending(true);
        try {
            const htmlBody = `<div style="font-family:Arial,sans-serif;font-size:14px;color:#333;line-height:1.6;">${composeForm.body.replace(/\n/g, '<br>')}</div>`;
            await axios.post(`${API_URL}/api/emails/send`, {
                to_email: composeForm.to,
                subject: composeForm.subject,
                body_html: htmlBody,
            }, { withCredentials: true });
            toast.success('Email envoye avec succes');
            setShowCompose(false);
            setComposeForm({ to: '', subject: '', body: '' });
            fetchSentEmails(true);
        } catch {
            toast.error("Erreur lors de l'envoi");
        } finally { setComposeSending(false); }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="messagerie-page">
                {/* Header */}
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {selectedEmail && (
                            <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setSelectedEmail(null)} data-testid="back-to-list">
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        )}
                        <Mail className="w-6 h-6 text-[#1B365D]" />
                        <h1 className="text-lg md:text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="messagerie-title">
                            Messagerie
                        </h1>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <Button variant="default" size="sm" className="gap-1.5 h-8 text-xs bg-[#1B365D] text-white hover:bg-[#2A4A7A]" data-testid="folder-sent">
                            <SendHorizonal className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Emails envoyes</span>
                            <span className="sm:hidden">Envoyes</span>
                        </Button>
                        <div className="w-px h-6 bg-gray-200 mx-1 hidden sm:block" />
                        <Button variant="outline" size="sm" onClick={() => fetchSentEmails(true)} disabled={refreshing} className="gap-1.5 h-8 text-xs" data-testid="refresh-emails">
                            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Actualiser</span>
                        </Button>
                        <Button size="sm" onClick={() => setShowCompose(true)}
                            disabled={!canWriteMail}
                            title={!canWriteMail ? 'Accès refusé : permission Écriture requise' : 'Composer'}
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1.5 h-8 text-xs disabled:opacity-50 disabled:cursor-not-allowed" data-testid="compose-email">
                            <PenSquare className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Nouveau message</span>
                        </Button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-0">
                    {/* Email List */}
                    <div className={`w-full md:w-[360px] lg:w-[400px] border-r border-gray-200 flex flex-col ${selectedEmail ? 'hidden md:flex' : 'flex'}`}>
                        <div className="p-3 border-b border-gray-100">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Rechercher par nom, email, sujet..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 h-9 bg-gray-50 border-gray-200 text-sm"
                                    data-testid="search-emails"
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center h-40">
                                    <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                                </div>
                            ) : filteredEmails.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                                    <SendHorizonal className="h-8 w-8 mb-2" />
                                    <p className="text-sm">{searchTerm ? 'Aucun resultat' : 'Aucun email envoye'}</p>
                                </div>
                            ) : filteredEmails.map((em, i) => {
                                const isSelected = selectedEmail?.id === em.id;
                                const displayName = em.client_name || em.to_email;
                                return (
                                    <div
                                        key={em.id || i}
                                        onClick={() => setSelectedEmail(em)}
                                        data-testid={`email-item-${i}`}
                                        className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 transition-colors
                                            ${isSelected ? 'bg-[#1B365D]/5 border-l-[3px] border-l-[#1B365D]' : 'hover:bg-gray-50'}`}
                                    >
                                        <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 text-[#1B365D] flex items-center justify-center text-sm font-semibold shrink-0">
                                            {getInitials(displayName)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-medium text-gray-900 truncate">
                                                    {displayName}
                                                </span>
                                                <span className="text-[10px] text-gray-400 shrink-0">{formatDate(em.sent_at)}</span>
                                            </div>
                                            <p className="text-xs text-gray-500 truncate mt-0.5">{em.to_email}</p>
                                            <p className="text-xs text-gray-400 truncate mt-0.5">{em.subject || '(Sans objet)'}</p>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-gray-300 mt-1 shrink-0" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Email Detail */}
                    <div className={`flex-1 flex flex-col ${selectedEmail ? 'flex' : 'hidden md:flex'}`}>
                        {!selectedEmail ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                                <MailOpen className="h-12 w-12 mb-3 text-gray-300" />
                                <p className="text-sm font-medium">Selectionnez un email</p>
                                <p className="text-xs mt-1">Cliquez sur un email pour voir les details</p>
                            </div>
                        ) : (
                            <>
                                {/* Email Header */}
                                <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/50 flex items-center gap-3">
                                    <Button variant="ghost" size="icon" className="md:hidden h-8 w-8 shrink-0" onClick={() => setSelectedEmail(null)}>
                                        <ArrowLeft className="h-4 w-4" />
                                    </Button>
                                    <div className="w-9 h-9 rounded-full bg-[#1B365D] text-white flex items-center justify-center text-sm font-semibold shrink-0">
                                        {getInitials(selectedEmail.client_name || selectedEmail.to_email)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-sm text-[#1B365D] truncate" data-testid="email-contact-name">
                                            {selectedEmail.client_name || selectedEmail.to_email}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">{selectedEmail.to_email}</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <Send className="h-3.5 w-3.5 text-[#C9A962]" />
                                        <span className="text-xs text-gray-500">Envoye</span>
                                    </div>
                                </div>

                                {/* Email Content */}
                                <div className="flex-1 overflow-y-auto p-5 bg-gray-50/30">
                                    <div className="max-w-2xl mx-auto">
                                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                            {/* Subject bar */}
                                            <div className="px-5 py-3 border-b border-gray-100 bg-[#1B365D]/[0.02]">
                                                <h2 className="text-sm font-semibold text-[#1B365D]">{selectedEmail.subject || '(Sans objet)'}</h2>
                                                <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                                                    <Clock className="w-3 h-3" />
                                                    <span>{formatFullDate(selectedEmail.sent_at)}</span>
                                                    <span className="mx-1">-</span>
                                                    <span>vers {selectedEmail.to_email}</span>
                                                </div>
                                            </div>
                                            {/* Body */}
                                            <div className="px-5 py-4">
                                                {selectedEmail.html_body ? (
                                                    <div
                                                        className="prose prose-sm max-w-none text-gray-700"
                                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmail.html_body, { USE_PROFILES: { html: true } }) }}
                                                    />
                                                ) : (
                                                    <p className="text-sm text-gray-500 italic">Aucun contenu</p>
                                                )}
                                            </div>
                                            {/* Type badge */}
                                            {selectedEmail.type && selectedEmail.type !== 'manual' && (
                                                <div className="px-5 py-2 border-t border-gray-100 bg-gray-50/50">
                                                    <span className="text-[10px] font-medium text-[#C9A962] bg-[#C9A962]/10 px-2 py-0.5 rounded-full">
                                                        {selectedEmail.type === 'rdv_confirmation' ? 'Confirmation RDV' : selectedEmail.type}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Compose Dialog */}
            <Dialog open={showCompose} onOpenChange={setShowCompose}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <PenSquare className="h-5 w-5" /> Nouveau message
                        </DialogTitle>
                        <DialogDescription>Envoyer un email depuis contact@luxymove.fr</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCompose}>
                        <div className="space-y-3 py-3">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700">Destinataire *</label>
                                <Input type="email" value={composeForm.to} onChange={(e) => setComposeForm(prev => ({ ...prev, to: e.target.value }))} placeholder="email@example.com" required data-testid="compose-to" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700">Objet *</label>
                                <Input value={composeForm.subject} onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))} placeholder="Objet du message" required data-testid="compose-subject" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700">Message</label>
                                <Textarea value={composeForm.body} onChange={(e) => setComposeForm(prev => ({ ...prev, body: e.target.value }))} placeholder="Votre message..." rows={6} data-testid="compose-body" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowCompose(false)}>Annuler</Button>
                            <Button type="submit" disabled={composeSending} className="bg-[#1B365D] hover:bg-[#2A4A7A] gap-1.5" data-testid="compose-send-btn">
                                <Send className="h-4 w-4" />
                                {composeSending ? 'Envoi...' : 'Envoyer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
